import { compose } from 'redux';
import { connect } from 'react-redux';
import MicTestCard from './MicTestCard';
import { getTrcInstruction, getTrcCaption, TRC_ERROR_AUDIO } from '../../../troConstants';
import AvatarView from '../../../../common/avatar/avatarView';
import withAvatar from '../../../../common/avatar/withAvatar';
import { narrateReset, stopAudio, setCaption } from '../../../../common/audio/audioActions';
import {
    blockScreen,
    unblockScreen,
    unmountScreen,
    setShouldDisplayCaption,
} from '../../../../common/screen/screenActions';
import { narrateScreen } from '../../../trcActions';
import { trackTroMicCheck, trackTroError, trackFailedMicCheck } from '../../../trcReporting';
import selectors from '../../../../rootSelectors';
import {
    clearMicTestAttemptCount,
    clearRecordedResult,
    incrementMicTestAttemptCount,
    setMicCheckPassed,
    setRecordedResult,
} from '../../../../common/recording/recordingActions';
import {
    getVerificationResult,
    setVerificationResult,
    setVerificationStatus,
} from '../../../../common/api/trcoActions';
import { playAvatarAudio, setAvatarInstructions } from '../../../../common/avatar/avatarActions';
import { VERIFICATION_STATUS } from '../TROInstructionsConstants';
import { setShouldPreventAlexAnimation } from '../../../../common/alex/AlexActions';

const instructionsKey = 'trcMicTesting';
const trcMicTestRetryInstructionKey = 'trcMicTestRetry';
const trcMicTestSuccessInstructionKey = 'trcMicTestSuccess';

// Plays a mic-test failure voiceover. Only the external (Boost Benchmark / AR)
// path blocks the screen during playback so the modal's action button stays
// gated until the VO ends; the standard TRC path just plays the VO, leaving its
// pre-existing behavior unchanged.
const playMicFailureVo = (dispatch, audio, isExternalURLSourcePresent) => {
    if (!isExternalURLSourcePresent) {
        dispatch(playAvatarAudio(audio));
        dispatch(setAvatarInstructions(audio));
        return;
    }
    dispatch(setShouldPreventAlexAnimation(true));
    dispatch(blockScreen());
    dispatch(playAvatarAudio(audio)).then(() => {
        dispatch(unblockScreen());
        dispatch(setShouldPreventAlexAnimation(false));
    });
    dispatch(setAvatarInstructions(audio));
};

const mapStateToProps = state => ({
    recordingStream: selectors.getRecordingStream(state),
    isRecordingInProgress: selectors.getIsRecordingInProgress(state),
    verificationStatus: selectors.selectVerificationStatus(state),
    verificationResult: selectors.selectVerificationResult(state),
    isScreenBlocked: selectors.getIsScreenBlocked(state),
    attemptCount: selectors.getMicTestAttemptCount(state),
    isSpinnerVisible: selectors.getIsSpinnerVisible(state),
    isExternalURLSourcePresent: selectors.getExternalURLSourceStatus(state),
    arLanguage: selectors.getArLanguage(state),
    isBenchmarkModeEnabled: selectors.getisBenchmarkModeEnabled(state),
});

const mapDispatchToProps = dispatch => ({
    narrateScreen: (isExternalURLSourcePresent, arLanguage) => {
        const instruction = getTrcInstruction(
            isExternalURLSourcePresent,
            instructionsKey,
            arLanguage,
        );
        const caption = getTrcCaption(isExternalURLSourcePresent, instructionsKey, arLanguage);
        dispatch(narrateScreen(instruction, caption));
    },
    cleanup: () => {
        dispatch(clearRecordedResult());
        dispatch(stopAudio());
        dispatch(narrateReset());
        dispatch(unmountScreen());
        dispatch(setVerificationResult({ reset_verification: true }));
        dispatch(setVerificationStatus(VERIFICATION_STATUS.IDLE));
        dispatch(clearMicTestAttemptCount());
    },
    onFirstTestFailure: (isExternalURLSourcePresent, arLanguage) => {
        const audio = getTrcInstruction(
            isExternalURLSourcePresent,
            trcMicTestRetryInstructionKey,
            arLanguage,
        );
        const caption = getTrcCaption(
            isExternalURLSourcePresent,
            trcMicTestRetryInstructionKey,
            arLanguage,
        );
        dispatch(blockScreen());
        if (isExternalURLSourcePresent) {
            dispatch(trackTroError('Failure on first mic test'));
            dispatch(setCaption(caption));
            dispatch(setShouldDisplayCaption(true));
        }
        dispatch(playAvatarAudio(audio)).then(() => {
            dispatch(unblockScreen());
            if (isExternalURLSourcePresent) {
                dispatch(setShouldDisplayCaption(false));
            }
        });
        dispatch(setAvatarInstructions(audio));
    },
    onSecondTestFailure: isExternalURLSourcePresent => {
        if (isExternalURLSourcePresent) {
            dispatch(trackTroError('Failure on second mic test'));
        }
        playMicFailureVo(
            dispatch,
            TRC_ERROR_AUDIO.trcMicVerificationFailure,
            isExternalURLSourcePresent,
        );
    },
    onThirdTestFailure: isExternalURLSourcePresent => {
        if (isExternalURLSourcePresent) {
            dispatch(trackTroMicCheck());
            dispatch(trackTroError('Failure on third mic test'));
        }
        playMicFailureVo(dispatch, TRC_ERROR_AUDIO.thatDidntWorkOne, isExternalURLSourcePresent);
    },
    onTestSuccess: (isExternalURLSourcePresent, arLanguage) => {
        const audio = getTrcInstruction(
            isExternalURLSourcePresent,
            trcMicTestSuccessInstructionKey,
            arLanguage,
        );
        const caption = getTrcCaption(
            isExternalURLSourcePresent,
            trcMicTestSuccessInstructionKey,
            arLanguage,
        );
        dispatch(blockScreen());
        if (isExternalURLSourcePresent) {
            dispatch(setCaption(caption));
            dispatch(setShouldDisplayCaption(true));
        }
        dispatch(playAvatarAudio(audio)).then(() => {
            dispatch(unblockScreen());
            if (isExternalURLSourcePresent) {
                dispatch(setShouldDisplayCaption(false));
            }
        });
        dispatch(setAvatarInstructions(audio));
        dispatch(setMicCheckPassed());
    },
    updateVerificationProgress: isExternalURLSourcePresent => {
        if (isExternalURLSourcePresent) {
            dispatch(setShouldPreventAlexAnimation(isExternalURLSourcePresent));
        }
        dispatch(clearRecordedResult());
        dispatch(setVerificationResult({ reset_verification: true }));
        dispatch(setVerificationStatus(VERIFICATION_STATUS.IDLE));
        dispatch(incrementMicTestAttemptCount());
    },
    onStopRecording: isExternalURLSourcePresent => {
        if (isExternalURLSourcePresent) {
            dispatch(setShouldPreventAlexAnimation(!isExternalURLSourcePresent));
        }
    },
    playAvatarAudio: audio => {
        dispatch(playAvatarAudio(audio));
        dispatch(setAvatarInstructions(audio));
    },
    saveRecordedResult: async recordedResult => {
        await dispatch(setRecordedResult(recordedResult));
        dispatch(getVerificationResult());
    },
    trackTroMicCheck: () => dispatch(trackTroMicCheck()),
    trackFailedMicCheck: attempt => dispatch(trackFailedMicCheck(attempt)),
});

const avatarMapStateToProps = state => ({
    position: AvatarView.Position.MIDDLE_LEFT,
    instructions: getTrcInstruction(
        selectors.getExternalURLSourceStatus(state),
        instructionsKey,
        selectors.getArLanguage(state),
    ),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAvatar(avatarMapStateToProps),
)(MicTestCard);
